import { Loader } from "@googlemaps/js-api-loader";
import { isEmpty } from "lodash";
import { searchEvent, SearchFilterEventEventData, searchFilterEventUpdate, searchFilterResetEvent } from "../search/search-events";
import { AddRouteToRoot, UpdatePaginationButtons } from "../search/search-helpers";
import LocationMapFilter from "./location-map-filter";
import { ContactType, Coordinates, Location, Suburb } from "./location-map-model";

export default class LocationMap {
    container: HTMLDivElement;
    // Default properties
    lat: number;
    lng: number;
    defaultZoom = 12;
    currentCountry: 'AU';
    countryId: {
        'AU': 'ChIJ38WHZwf9KysRUhNblaFnglM',
    };

    // Autocomplete
    query: string = '';
    autocompleteControllerURL: string;

    // Google maps elements
    AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
    InfoWindow: typeof google.maps.InfoWindow;

    googleMap: google.maps.Map;
    geocoder: google.maps.Geocoder;
    markers: google.maps.marker.AdvancedMarkerElement[] = [];
    bounds: google.maps.LatLngBounds;

    allLocations: Location[];
    filteredLocations: Location[];
    subcategories: HTMLLIElement[] = [];
    filteredListItems: HTMLElement[]
    allListItems: HTMLElement[]

    hasMainFilterChanged = false;
    CategoryFilter: LocationMapFilter;
    SubcategoryFilter: LocationMapFilter;
    SubcategoryFilterDsk: LocationMapFilter;

    // HTML Elements
    mapContainer: HTMLDivElement;
    suburbFilterInputWrapper: HTMLElement;
    suburbFilterInput: HTMLInputElement;
    autocompleteListWrapper: HTMLDivElement;
    autocompleteList: HTMLUListElement;
    subcategoryFilterButtons: NodeListOf<HTMLButtonElement>;
    subcategoryFilterWrapper: NodeListOf<HTMLDivElement>;
    useLocationButton: HTMLButtonElement;
    locationListContainer: HTMLDivElement;
    removeButton: HTMLButtonElement;
    listViewButtons: NodeListOf<HTMLElement>;
    collapseListButton: HTMLButtonElement;
    totalPages: number;
    currentPage: number = 1;
    itemsPerPage: number = 15;
    paginationContainer: HTMLElement;
    nextButton: HTMLButtonElement;
    prevButton: HTMLButtonElement;
    pageInfo: HTMLElement;
    categoryDisplayName: string;
    infoWindows: google.maps.InfoWindow[] = [];
    currentFocusIndex: number;

    constructor(
        containerId: string,
        private mapElement: HTMLDivElement,
        apiEndpoint: string,
        private defaultCategoryId: number,
        private categoryFilterId: string,
        private subcategoryFilterId: string,
        private subcategoryFilterDskId: string,
        private mapsApiKey: string,
        private defaultSubCategoryId: string = "0"
    ) {
        this.mapContainer = this.mapElement.parentElement as HTMLDivElement;
        this.container = document.getElementById(containerId) as HTMLDivElement;
        this.autocompleteControllerURL = AddRouteToRoot(apiEndpoint);
        if (!this.mapElement) return;

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('location') || urlParams.has('category') || urlParams.has('subcategory')) {
            history.replaceState({}, '', location.pathname); 
        }
        this.initAsync();    
    }

    async initAsync() {
        this.initElements();

        const locationData = this.mapElement.getAttribute("data-location");
        this.allLocations = JSON.parse(locationData);

        // Init map elements
        this.lat = Number(this.mapElement.getAttribute("data-latitude"));
        this.lng = Number(this.mapElement.getAttribute("data-longitude"));

        this.googleMap = await this.initMap(this.mapElement);
        this.googleMap.addListener('tilesloaded', () => {
            if (this.geocoder) return; // Only load once
            this.geocoder = new google.maps.Geocoder();
            const controlButton = this.createCustomControlButton();
            this.googleMap.controls[google.maps.ControlPosition.INLINE_END_BLOCK_END].push(controlButton);

            // Custom events
            this.addEventListeners();
            this.updateSubcategoryFilter(this.defaultCategoryId);
            this.updateMap(this.defaultCategoryId.toString(), 'category', this.defaultSubCategoryId);
            this.initFilters(this.categoryFilterId, this.subcategoryFilterId, this.subcategoryFilterDskId);
            this.updateList(this.lat, this.lng);
            this.collapseListButton.classList.add('loaded');
            this.updateCategoryDisplay();
        });
    }

    private initElements() {
        this.suburbFilterInputWrapper = this.container.querySelector<HTMLElement>(".input-wrapper");
        this.suburbFilterInput = this.container.querySelector<HTMLInputElement>("#suburb-autocomplete");
        this.autocompleteListWrapper = this.container.querySelector(".autocomplete-wrapper");
        this.autocompleteList = this.container.querySelector(".autocomplete-list");
        this.subcategoryFilterButtons = this.container.querySelectorAll(".subcategory-filter");
        this.subcategoryFilterWrapper = document.querySelectorAll(".location-map--header .dropdown-menu, #map-filter__offcanvas .dropdown-menu");
        this.useLocationButton = this.autocompleteListWrapper.querySelector("button#use-location");
        this.locationListContainer = this.container.querySelector(".location-list-container");
        this.allListItems = Array.from(this.locationListContainer.querySelectorAll<HTMLDivElement>('.location-item'));
        this.subcategoryFilterWrapper.forEach(wrapper => {
            const listItems = wrapper.querySelectorAll<HTMLLIElement>('.dropdown-item');
            listItems?.forEach(li => this.subcategories.push(li));
        });
        this.removeButton = this.container.querySelector(".remove-button");
        this.collapseListButton = this.container.querySelector<HTMLButtonElement>(".collapse__button");

        this.paginationContainer = this.container.querySelector<HTMLElement>('.location-pagination');
        this.prevButton = this.paginationContainer.querySelector<HTMLButtonElement>('.pagination__prev .button');
        this.nextButton = this.paginationContainer.querySelector<HTMLButtonElement>('.pagination__next .button');
        this.pageInfo = this.paginationContainer.querySelector<HTMLElement>('.pagination__buttons');
    }

    /**
     * Initialise map with default center location
     * @method initMap
     * @return google.maps.Map | null
     */

    private async initMap(mapElement: HTMLDivElement): Promise<google.maps.Map | null> {
        try {
            if (!mapElement) return null;

            const loader = new Loader({
                apiKey: this.mapsApiKey
            });

            const { Map, MapTypeId, InfoWindow } = await loader.importLibrary("maps") as google.maps.MapsLibrary;
            const { AdvancedMarkerElement } = await loader.importLibrary("marker") as google.maps.MarkerLibrary;
            this.AdvancedMarkerElement = AdvancedMarkerElement;
            this.InfoWindow = InfoWindow;

            const mapID = mapElement.getAttribute("data-mapId");

            const mapCenterCoordinates: google.maps.LatLngLiteral = {
                lat: this.lat,
                lng: this.lng
            };

            const googleMapOptions = {
                mapId: mapID,
                center: mapCenterCoordinates,
                zoom: this.defaultZoom,
                disableDefaultUI: true,
                keyboardShortcuts: false,
                scrollwheel: null,
                zoomControl: true,
                gestureHandling: 'cooperative',  // disables default scroll zoom
                mapTypeId: MapTypeId.ROADMAP
            } as google.maps.MapOptions;

            const googleMap = new Map(mapElement, googleMapOptions);

            return googleMap;

        } catch (err) {
            console.error(err)
            return null;
        }
    }

    private initFilters(categoryFilterId: string, subcategoryFilterId: string, subcategoryFilterDskId: string) {
        this.CategoryFilter = new LocationMapFilter(categoryFilterId, 'category', false, false);
        this.SubcategoryFilter = new LocationMapFilter(subcategoryFilterId, 'subcategory', true, false);
        this.SubcategoryFilterDsk = new LocationMapFilter(subcategoryFilterDskId, 'subcategory', true, false);
    }

    private addEventListeners() {
        this.currentFocusIndex = 0;
        const mapViewButton = this.container.querySelector<HTMLButtonElement>(".switch-button#map");
        const listViewButton = this.container.querySelector<HTMLButtonElement>(".switch-button#list");

        this.suburbFilterInput.addEventListener('input', async () => {
            this.query = this.suburbFilterInput.value;
            if (this.query.length > 0) {
                const response = await fetch(`${this.autocompleteControllerURL}?query=${encodeURIComponent(this.query)}`);
                const results = await response.json();
                this.displaySuggestions(results);
                this.showAutocompleteList();

                this.removeButton.classList.add("show");
            } else {
                this.autocompleteList.innerHTML = '';
                this.removeButton.classList.remove("show");
            }
        });

        this.getInitialSuburb();

        this.suburbFilterInput.addEventListener('focus', () => {
            this.showAutocompleteList();
        });

        this.suburbFilterInputWrapper.addEventListener('focusout', (e: FocusEvent) => {
            const target = e.relatedTarget as HTMLElement;

            if (target && !this.suburbFilterInputWrapper.contains(target)) {
                this.hideAutocompleteList();
            }
        });

        this.suburbFilterInputWrapper.addEventListener('keydown', (e: KeyboardEvent) => {
            const allFocusableElements = Array.from(this.autocompleteListWrapper.querySelectorAll<HTMLElement>('li[tabindex="0"], button'));

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (allFocusableElements.length > 0) {
                        this.currentFocusIndex = (this.currentFocusIndex + 1) % allFocusableElements.length;
                        allFocusableElements[this.currentFocusIndex].focus();
                    }
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    if (allFocusableElements.length > 0) {
                        this.currentFocusIndex = (this.currentFocusIndex - 1 + allFocusableElements.length) % allFocusableElements.length;
                        allFocusableElements[this.currentFocusIndex].focus();
                    }
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (this.currentFocusIndex >= 0 && this.currentFocusIndex < allFocusableElements.length) {
                        allFocusableElements[this.currentFocusIndex].click();
                    }
                    break;
                case 'Escape':
                    this.hideAutocompleteList();
                    const activeEl = document.activeElement as HTMLElement;
                    if (allFocusableElements.includes(activeEl)) {
                        activeEl.blur();
                    }
                    this.suburbFilterInput.blur();
                    this.currentFocusIndex = -1;
                    break;
            }
        });

        this.removeButton.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.removeButton.click();
            }
        });

        this.removeButton.addEventListener('click', () => {
            this.suburbFilterInput.value = "";
            this.autocompleteList.innerHTML = '';
            this.removeButton.classList.remove("show");
            this.suburbFilterInput.focus();
            this.hideAutocompleteList();
            this.resetMap();
            this.updateList(this.lat, this.lng);
            this.changePage(1);
            this.updateIndicatorText(`Perth, 6000`);
        });

        this.useLocationButton.addEventListener('click', () => {
            if (!navigator.geolocation) {
                this.setClientLocation();
            }
            navigator.geolocation.getCurrentPosition((position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }
                this.getSuburbByLatLng(pos);
            })
            this.hideAutocompleteList();
        });

        listViewButton.addEventListener('click', () => {
            this.mapContainer.classList.remove('map-view');
            this.mapContainer.classList.add('list-view');
            mapViewButton.classList.remove('active');
            listViewButton.classList.add('active');
            mapViewButton.setAttribute('aria-pressed', 'false');
            listViewButton.setAttribute('aria-pressed', 'true');
            this.collapseListButton.style.display = 'none';
            this.updateLocationButtonsForListView();
        });

        mapViewButton.addEventListener('click', () => {
            this.mapContainer.classList.add('map-view');
            this.mapContainer.classList.remove('list-view');
            mapViewButton.classList.add('active');
            listViewButton.classList.remove('active');
            mapViewButton.setAttribute('aria-pressed', 'true');
            listViewButton.setAttribute('aria-pressed', 'false');
            this.collapseListButton.style.display = 'block';
            this.updateLocationButtonsForMapView();
        });

        const clearButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.clear-button'));
        clearButtons?.forEach(btn => btn.disabled = this.defaultSubCategoryId.split(",").map(Number).filter(num => num != 0).length == 0);

        searchEvent.on(searchFilterEventUpdate, (updateEventData: SearchFilterEventEventData) => {
            setTimeout(() => {
                const selectedId = updateEventData.searchValue.split(';')[0];
                const categoryId = this.container.querySelector('#category-filter button').getAttribute('data-itemid');
                this.categoryDisplayName = this.container.querySelector<HTMLElement>(`#category-filter .dropdown-item[data-id="${categoryId}"]`)?.getAttribute('data-name') ?? this.categoryDisplayName;
                let subCategoryIds = "0";
                if (updateEventData.searchValue == "") {
                    this.updateMap(categoryId, "category", subCategoryIds);
                } else {
                    if (updateEventData.searchKey == "category") {
                        const selectedItemName = this.container.querySelector(`#category-filter .dropdown-item[data-id='${selectedId}']`).getAttribute('data-name');
                        this.updateSubcategoryFilter(Number(selectedId), selectedItemName);
                        this.SubcategoryFilter.Reset();
                        this.SubcategoryFilterDsk.Reset();
                    } else {
                        subCategoryIds = selectedId;
                    }

                    if (updateEventData.searchKey == "category" || updateEventData.searchKey == "subcategory") {
                        this.updateMap(categoryId, updateEventData.searchKey, subCategoryIds);
                    }
                }

                clearButtons?.forEach(btn => btn.disabled = updateEventData.searchValue === "");

                if (updateEventData.searchKey !== "subcategory") {
                    this.focusFirstElement();
                }
            }, 10)
        });

        searchEvent.on(searchFilterResetEvent, () => {
            this.suburbFilterInput.value = "";
            this.updateIndicatorText(`Perth, 6000`);
            this.resetMap();
            this.updateMap(this.container.querySelector('#category-filter button').getAttribute('data-itemid'), "category", "0");
            this.updateList(this.lat, this.lng);
            this.focusFirstElement();
        });

        this.googleMap.addListener('bounds_changed', () => this.closeOutOfBoundsWindows());
        this.listViewButtons = this.container.querySelectorAll<HTMLElement>('.gotopage__button a.button');
        window.addEventListener('resize', () => this.updateListButtonSize());
        this.updateListButtonSize();

        this.collapseListButton?.addEventListener('click', () => this.collapseList());
        this.prevButton.addEventListener('click', () => this.changePage(this.currentPage - 1));
        this.nextButton.addEventListener('click', () => this.changePage(this.currentPage + 1));
    }
    
    private updateFocusIndex() {
        const allFocusableElements = Array.from(this.autocompleteListWrapper.querySelectorAll<HTMLElement>('li[tabindex="0"], button'));
        allFocusableElements.forEach((element, index) => {
            element.addEventListener('focus', () => {
                this.currentFocusIndex = index;
            });
        });
    }

    private updateLocationButtonsForListView() {
        const list: HTMLDivElement | null = this.container.querySelector<HTMLDivElement>('.location-list');
        const locationButtons: NodeListOf<HTMLButtonElement> = list.querySelectorAll<HTMLButtonElement>('.location-name');

        if (!list) return;

        locationButtons.forEach((button, index) => {
            const buttonInfo: HTMLSpanElement | null = button.nextElementSibling as HTMLSpanElement;

            if (!buttonInfo || buttonInfo.id !== `button-info-${index}`) {
                button.insertAdjacentHTML('afterend', `<span id="button-info-${index}" class="visually-hidden">Enable map view to activate</span>`);
            }

            button.setAttribute('aria-describedby', `button-info-${index}`)
            button.setAttribute('aria-disabled', 'true');
        })
    }

    private updateLocationButtonsForMapView() {
        const list: HTMLDivElement | null = this.container.querySelector<HTMLDivElement>('.location-list');
        const locationButtons: NodeListOf<HTMLButtonElement> = list.querySelectorAll<HTMLButtonElement>('.location-name');

        if (!list) return;

        locationButtons.forEach((button, index) => {
            const buttonInfo: HTMLSpanElement | null = button.nextElementSibling as HTMLSpanElement;

            if (buttonInfo && buttonInfo.id === `button-info-${index}`) {
                buttonInfo.remove();
            }

            button.removeAttribute('aria-describedby');
            button.removeAttribute('aria-disabled');
        })
    }

    private updateListButtonSize(): void {
        const smallScreen = window.innerWidth < 992;
        this.listViewButtons.forEach(button => button.classList.toggle("button--lg", !button.classList.toggle("button--sm", smallScreen)));
    }

    private collapseList(): void {
        const list = this.container.querySelector<HTMLElement>('.location-list');

        list.classList.toggle('collapsed_list');
        this.collapseListButton.classList.toggle('collapsed');

        const isExpanded = this.collapseListButton.getAttribute('aria-expanded') === 'true';
        this.collapseListButton.setAttribute('aria-expanded', (!isExpanded).toString());
    }

    private engageListButtonMapAnchors() {
        this.allListItems.forEach(listItem => {
            const itemLat = Number(listItem.getAttribute('data-lat'));
            const itemLng = Number(listItem.getAttribute('data-lng'));
            const locationHeading = listItem.querySelector<HTMLButtonElement>('.location-name');
            const locationMarker = this.markers.find(marker => marker.position.lat === itemLat && marker.position.lng === itemLng);
            locationHeading.disabled = !locationMarker;

            if (locationMarker)
                locationHeading?.addEventListener('click', () => locationMarker.element.click());
        });
    }

    private updateMap(filterVal: string, filterName: string, subCategoryIds: string = "0") {
        const subcategoryIdNumbers = subCategoryIds?.split(',').map(Number).filter(number => number != 0);
        if (filterName == 'category' || filterName == 'subcategory') {
            const categoryId = Number(filterVal);
            this.filteredLocations = this.allLocations.filter(loc => loc.categoryId.split('|').length != 0 && loc.categoryId.split('|').map(Number).some(id => id === categoryId));
            this.filteredListItems = this.allListItems.filter(listItem => listItem.getAttribute('data-categoryid')?.split('|').length != 0 && listItem.getAttribute('data-categoryid').split('|').some(id => id === filterVal));

            if (this.categoryDisplayName) this.updateCategoryHeaderText();
            if (subcategoryIdNumbers && subcategoryIdNumbers.length != 0) {
                this.filteredLocations = this.filteredLocations.filter(loc => loc.subcategoryId.split('|').length != 0 && loc.subcategoryId.split('|').map(Number).some(id => subcategoryIdNumbers.includes(id)));
                this.filteredListItems = this.filteredListItems.filter(li => li.getAttribute('data-subcategoryid')?.split('|').length != 0 && li.getAttribute('data-subcategoryid').split('|').map(Number).some(id => subcategoryIdNumbers.includes(id)));
            }
        }

        this.updateSubcategoryFilterCount(subcategoryIdNumbers?.length ?? 0);
        this.currentPage = 1;

        this.placeMarkers();
        const currentCenter = this.googleMap.getCenter();
        this.updateList(currentCenter.lat(), currentCenter.lng());
    }

    private updateList(lat: number = null, lng: number = null) {
        this.allListItems.forEach(li => li.classList.remove('show'));

        if (lat && lng)
            this.sortList(lat, lng);

        const startIdx = (this.currentPage - 1) * this.itemsPerPage;
        const endIdx = startIdx + this.itemsPerPage;
        const visibleItems = this.filteredListItems.slice(startIdx, endIdx);
        visibleItems.forEach(li => li.classList.add('show'));
        this.updatePaginationControls();
        this.engageListButtonMapAnchors();
        this.updateCategoryHeaderText();
    }

    private focusFirstElement() {
        const startIdx = (this.currentPage - 1) * this.itemsPerPage;
        const endIdx = startIdx + this.itemsPerPage;
        const visibleItems = this.filteredListItems.slice(startIdx, endIdx);

        if (visibleItems.length > 0) {
            const firstLocationInList: HTMLButtonElement = visibleItems[0].querySelector('button.location-name');

            if (firstLocationInList) {
                firstLocationInList.focus();
            }
        }
    }

    private changePage(targetPage: number) {
        this.currentPage = targetPage;
        this.updateList();
        this.focusFirstElement();
    }

    private updateInfoWindowButtons() {
        setTimeout(() => {
            const moreInfoButtons: NodeListOf<HTMLButtonElement> = this.container.querySelectorAll<HTMLButtonElement>('.licenced-agency.button');

            moreInfoButtons?.forEach(button => button.addEventListener('click', () => {
                const filteredListItem: HTMLElement | undefined = this.filteredListItems.find(item => item.getAttribute("data-contactname") === button.getAttribute("data-contactname"));
                const pageNumber: number = Math.floor(this.filteredListItems.indexOf(filteredListItem) / this.itemsPerPage) + 1;
                const contactInfo: HTMLElement | null = document.getElementById(button.getAttribute('data-contactname'));

                this.changePage(pageNumber);
                this.container.querySelector<HTMLButtonElement>('.switch-button#list').click();

                contactInfo.scrollIntoView({ behavior: 'smooth', block: 'center' });
                contactInfo.classList.add('clickedFromMap');

                if (contactInfo.classList.contains('clickedFromMap')) {
                    contactInfo.focus();
                }

                setTimeout(() => {
                    contactInfo.classList.remove('clickedFromMap');
                }, 5000);
            }));
        }, 300);
    }

    private updatePaginationControls() {
        this.totalPages = Math.ceil(this.filteredListItems.length / this.itemsPerPage);
        UpdatePaginationButtons(this.currentPage, this.currentPage === this.totalPages, this.totalPages, this.paginationContainer);
        this.updatePaginationButtons();
        this.paginationContainer.classList.toggle('show', this.totalPages != 0);
    }

    private updatePaginationButtons() {
        const paginationButtons = this.paginationContainer.querySelectorAll<HTMLButtonElement>('.pagination__button');
        paginationButtons?.forEach(button => button.addEventListener('click', () => this.changePage(Number(button.getAttribute('data-page')))));
    }

    private updateCategoryHeaderText(infoWindow: google.maps.InfoWindow = null, infoWindowContent: string = null) {
        if (infoWindow && infoWindowContent && infoWindowContent != "") {
            // Update newly opened window
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = infoWindowContent;

            const categoryHeader = tempDiv.querySelector<HTMLElement>('.info--service-type');
            if (categoryHeader) {
                const headerText = categoryHeader.getAttribute('data-default-text') ?? categoryHeader.textContent;

                if (headerText.toLowerCase().split(',').map(category => category.trim()).some(category => category == this.categoryDisplayName?.trim().toLowerCase())) {
                    categoryHeader.textContent = this.categoryDisplayName.toUpperCase();
                } else {
                    categoryHeader.textContent = headerText;
                }
            }

            // Set the modified content back to the infoWindow
            infoWindow.setContent(tempDiv.innerHTML);
        }
        else {
            // Update list
            this.allListItems.forEach(item => {
                const categoryHeader = item.querySelector<HTMLElement>('.location-type');
                const headerText = categoryHeader.getAttribute('data-default-text') ?? categoryHeader.textContent;

                if (headerText.toLowerCase().split(',').map(category => category.trim()).some(category => category == this.categoryDisplayName?.trim().toLowerCase())) {
                    categoryHeader.textContent = this.categoryDisplayName.toUpperCase();
                } else {
                    categoryHeader.textContent = headerText;
                }
            });
        }
    }

    private updateCategoryDisplay() {
        this.categoryDisplayName = this.container.querySelector<HTMLElement>(`#category-filter .dropdown-item`)?.getAttribute('data-name') ?? this.categoryDisplayName;
        this.updateCategoryHeaderText();
    }

    private async getInitialSuburb() {
        const initialValue = this.suburbFilterInput.getAttribute('data-default-value');
        if (isEmpty(initialValue)) return;

        this.query = initialValue;
        const response = await fetch(`${this.autocompleteControllerURL}?query=${encodeURIComponent(this.query)}`);
        const results = await response.json();
        const result = results[0];
        if (!result) return;

        const lat = result.lat;
        const lng = result.lng;
        this.suburbFilterInput.value = `${result.locality}, ${result.postcode}`
        this.suburbFilterInput.setAttribute('aria-expanded', 'false');
        this.autocompleteListWrapper.classList.remove('show');
        this.googleMap.setZoom(this.defaultZoom);
        this.googleMap.panTo({ lat, lng });
        this.updateIndicatorText(`${result.locality}, ${result.postcode}`);
        this.updateList(lat, lng);

        this.removeButton.classList.add("show");
    }

    private placeMarkers() {
        if (this.markers && this.markers.length != 0) this.clearMarkersAndClusters();

        let locations: Location[];

        locations = this.filteredLocations ?? this.allLocations;
        if (!locations || locations.length === 0) return;
        
        locations.forEach(location => {
            const isLicensingCentre = location.isLicensingCentre;
            const isDoTLicencingCentre = isLicensingCentre && location.type == ContactType.Department_Of_Transport;
            const isServiceWA = location.contactName.toLowerCase().includes("servicewa");
            const dotLogoImg = document.createElement('img');

            dotLogoImg.src = isServiceWA ? "/assets/media/servicewa-logo.svg" : "/assets/media/dot-marker-logo.svg";
            dotLogoImg.loading = "lazy";
            dotLogoImg.classList.add("dot-marker");
            dotLogoImg.setAttribute("alt", "");

            if (isServiceWA) {
                dotLogoImg.classList.add("service-wa");
            }

            if (location.isManualOutage) {
                dotLogoImg.classList.add("manual-outage");
            }

            let infoWindowContent = `
                <div id="focusElement" tabindex="-1">
                    <span class="info--service-type bold-label--xs" data-default-text="${location.serviceType.toUpperCase()}">${location.serviceType.toUpperCase()}</span>
                    <p class="bold-label--std h4">${location.contactName}</p>
                    <div class="info--address">
                        <p>${location.address1}</p>
                        <p>${location.address2}</p>
                        <a href="https://www.google.com/maps/dir/?api=1&destination=${location.coordinates.lat},${location.coordinates.lng}" target="_blank">Get directions</a>
                    </div>
                 `;

            if (isDoTLicencingCentre) {
                if (location.isManualOutage) {
                    infoWindowContent += `<p class="location-status">
                                        <span class="fa-solid fa-circle-exclamation"></span>
                                        <strong>Temporarily closed.</strong>
                                        <a href="${location.detailsPage}">Learn more</a>
                                    </p>`;
                }
                else {
                    infoWindowContent += `<span class="status">${location.operationalStatus}</span>`
                }
            }

            if (isDoTLicencingCentre) {
                infoWindowContent += `
                    <a href="${location.detailsPage}" class="button button_primary button--sm icon--right">
                    <span class="button-content">Go to Centre page</span>
                    <span class="button-icon dot-icon dot-stroke-forward-arrow" aria-hidden="true"></span>
                    </a>
                `;
            } else {
                infoWindowContent += `
                    <button type="button" data-contactname="${location.contactName}" class="licenced-agency button button_primary button--sm icon--right">
                    <span class="button-content">More information</span>
                    <span class="button-icon dot-icon dot-stroke-forward-arrow" aria-hidden="true"></span>
                    </button>
                `;
            }

            infoWindowContent += `</div>`;

            const marker = new this.AdvancedMarkerElement({
                position: new google.maps.LatLng(location.coordinates.lat, location.coordinates.lng),
                content: isDoTLicencingCentre ? dotLogoImg : null,
                map: this.googleMap,
                title: location.contactName,
                gmpClickable: true,
            });

            const infoWindow = new this.InfoWindow({
                content: infoWindowContent,
            });

            marker.addListener('gmp-click', () => {
                this.googleMap.panTo(marker.position);
                this.updateCategoryHeaderText(infoWindow, infoWindow.getContent() as string);
                infoWindow.open({anchor:marker, shouldFocus:false})
                this.updateInfoWindowButtons();
                this.closeOtherWindows(infoWindow);
            });

            google.maps.event.addListener(infoWindow, 'domready', () => {
                const focusElement = document.getElementById('focusElement');
                if (focusElement) {
                    focusElement.focus();
                }
            });

            this.infoWindows.push(infoWindow);
            this.markers.push(marker);
        });
    }

    private displaySuggestions(results: Suburb[]) {
        this.autocompleteList.innerHTML = '';
        results.forEach(result => {
            const li = document.createElement('li');
            li.setAttribute('data-lat', result.lat?.toString());
            li.setAttribute('data-lng', result.lng?.toString());
            const button = document.createElement('button');
            button.setAttribute('role', "option");
            button.type = 'button';
            button.innerHTML = highlightMatch(result.locality, this.query) + ", " + result.postcode;
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const lat = result.lat;
                const lng = result.lng;
                this.suburbFilterInput.value = `${result.locality}, ${result.postcode}`
                this.suburbFilterInput.setAttribute('aria-expanded', 'false');
                this.autocompleteListWrapper.classList.remove('show');
                this.googleMap.setZoom(this.defaultZoom);
                this.googleMap.panTo({ lat, lng });
                this.updateIndicatorText(`${result.locality}, ${result.postcode}`);
                this.updateList(lat, lng);
                this.removeButton.focus();
                this.autocompleteList.innerHTML = '';
                this.changePage(1);
            });

            li.appendChild(button);
            this.autocompleteList.appendChild(li);
        });
        function highlightMatch(text: string, query: string) {
            const regex = new RegExp(`(${query})`, 'gi');
            return text.replace(regex, '<strong>$1</strong>');
        }

        this.updateFocusIndex();
    }

    private updateSubcategoryFilter(categoryId: number, categoryName?: string) {
        const filteredSubcategories = this.subcategories.filter(x => x.getAttribute('data-parent-id') == categoryId.toString());
        const filterMenuLabels = document.querySelectorAll<HTMLElement>('.subcategory-filter-label');

        if (categoryName) filterMenuLabels.forEach(label => label.textContent = categoryName);

        this.subcategoryFilterButtons.forEach(btn => btn.classList.toggle('hide', filteredSubcategories.length == 0));
        this.subcategories.forEach(li => li.classList.remove('show'));
        filteredSubcategories.forEach(sc => sc.classList.toggle('show', sc.getAttribute('data-parent-id') == categoryId.toString()));
    }

    private updateSubcategoryFilterCount(filtersApplied: number = 0) {
        this.subcategoryFilterButtons?.forEach(filter => {
            const spanCount = filter.querySelector<HTMLSpanElement>('.filter-num');
            if (spanCount)
                spanCount.textContent = filtersApplied === 0 ? "" : `(${filtersApplied})`;
        });
    }

    private sortList(lat: number, lng: number): void {
        if (!this.locationListContainer) return;

        this.filteredListItems.sort((a, b) => {
            const aLat = parseFloat(a.getAttribute("data-lat"));
            const aLng = parseFloat(a.getAttribute("data-lng"));
            const bLat = parseFloat(b.getAttribute("data-lat"));
            const bLng = parseFloat(b.getAttribute("data-lng"));

            if (isNaN(aLat) || isNaN(aLng) || isNaN(bLat) || isNaN(bLng)) {
                console.warn("Invalid number passed");
                return 0;
            }
           
            const aDistance = this.getDistanceFromLatLonInKm(lat, lng, aLat, aLng);
            const bDistance = this.getDistanceFromLatLonInKm(lat, lng, bLat, bLng);

            return aDistance - bDistance;

        });

        this.locationListContainer.innerHTML = "";
        this.filteredListItems.forEach((item) => this.locationListContainer.appendChild(item));
        this.locationListContainer.appendChild(this.paginationContainer);

        // Scroll first item into view
        const firstItem = this.container.querySelector<HTMLElement>('.map-container.list-view .location-list-container .show :first-child, .map-container.map-view .location-list-container .show :first-child');
        firstItem?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    private setClientLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                this.googleMap.setCenter(pos);
            });
        }
    }

    private createCustomControlButton() {
        const controlButton = document.createElement('button') as HTMLButtonElement;
        controlButton.classList.add('compass-button');
        controlButton.type = "button";
        controlButton.style.backgroundColor = '#fff';
        controlButton.style.border = '2px solid #fff';
        controlButton.style.borderRadius = '4px';
        controlButton.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        controlButton.innerHTML = "<span class='visually-hidden'>Use my location</span><img alt='' aria-hidden='true' src='/assets/media/crosshairs-icon.svg' class='icon'/>"
        controlButton.addEventListener('click', () => this.setClientLocation());
        return controlButton;
    }

    //****** Helper functions ******//
    private getSuburbByLatLng(latlng: Coordinates) {
        this.geocoder
            .geocode({
                location: latlng,
            })
            .then((response) => {
                if (response.results[0]) {
                    const { suburb, postcode }: { suburb: string, postcode: string } = this.getFirstSuburbAndPostcode(response.results);

                    this.suburbFilterInput.value = `${suburb}, ${postcode}`;
                    this.updateIndicatorText(`${suburb}, ${postcode}`);

                } else {
                    window.alert("No results found");
                }
            })
            .catch((e) => window.alert("Geocoder failed due to: " + e));
    }

    private getFirstSuburbAndPostcode(results: google.maps.GeocoderResult[]) {
        for (const result of results) {
            let suburb = null;
            let postcode = null;

            for (let component of result.address_components) {
                if (component.types.includes("locality")) {
                    suburb = component.long_name;
                }
                if (component.types.includes("postal_code")) {
                    postcode = component.long_name;
                }
                if (suburb && postcode) {
                    return { suburb, postcode };
                }
            }
        }

        return { suburb: "Suburb not found", postcode: "Postcode not found" };
    }

    private getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const deg2rad = (deg: number) => deg * (Math.PI / 180);

        const R = 6371; // Radius of the earth in km
        const dLat = deg2rad(lat2 - lat1);  // deg2rad below
        const dLon = deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c; // Distance in km

        return distance;
    }

    private clearMarkersAndClusters(): void {
        this.markers.forEach((marker: google.maps.marker.AdvancedMarkerElement) => {
            marker.map = null;
        });
        this.markers = [];
    }

    private resetMap() {
        this.googleMap.setCenter({ lat: this.lat, lng: this.lng });
        this.googleMap.setZoom(this.defaultZoom);
    }

    private hideAutocompleteList() {
        this.suburbFilterInput.setAttribute('aria-expanded', 'false');
        this.autocompleteListWrapper.classList.remove('show');
    }

    private showAutocompleteList() {
        this.suburbFilterInput.setAttribute('aria-expanded', 'true');
        this.autocompleteListWrapper.classList.add('show');
    }

    private updateIndicatorText(suburb: string) {
        const indicator = this.container.querySelector(".location-list-indicator");
        indicator.textContent = `Displaying results closest to ${suburb}`;
    }

    private closeOutOfBoundsWindows() {
        const openInfoWindows = this.infoWindows.filter(window => window.isOpen);
        const mapBounds = this.googleMap.getBounds();
        if (!openInfoWindows || !mapBounds) return;

        openInfoWindows?.forEach(window => {
            if (!mapBounds.contains(window.getPosition()))
                window.close();
        });
    }

    private closeOtherWindows(openedWindow: google.maps.InfoWindow) {
        const openInfoWindows = this.infoWindows.filter(window => window.isOpen);
        
        openInfoWindows?.forEach(window => {
            if (window != openedWindow)
                window.close();
        });
    }
}
